/**
 * AffiliateReferralProgramPage — multi-level referral program console.
 *
 * BE: /api/v1/affiliate-referral-program.
 *   - GET/PUT /program                 — tenant program config
 *   - GET/POST /referrals              — recruit edges
 *   - PUT /referrals/:id               — activate/revoke edges
 *   - GET /ledger                      — bonus ledger rows
 *   - GET /summary/:affiliate          — per-affiliate totals
 *
 * Brand: emerald primary, zinc borders, emerald->sky gradient H1 only.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAffiliateReferralCreate,
	useAffiliateReferralLedger,
	useAffiliateReferralProgram,
	useAffiliateReferralProgramUpsert,
	useAffiliateReferralSummary,
	useAffiliateReferralUpdate,
	useAffiliateReferralsList,
} from "./hooks";
import type { ReferralStatus, Tier } from "./api";

const CARD = "rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT = INPUT;
const LABEL = "block text-xs font-medium text-zinc-700 mb-1";
const BTN =
	"rounded-md bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50";
const BTN_OUTLINE =
	"rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50";
const BTN_GHOST =
	"rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-3 text-sm text-zinc-900";

function formatCents(cents: number | null | undefined): string {
	if (cents === null || cents === undefined) return "—";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.floor(abs / 100);
	const rem = abs % 100;
	return `${sign}$${dollars.toLocaleString()}.${rem.toString().padStart(2, "0")}`;
}

function formatUnix(ts: number | null | undefined): string {
	if (!ts) return "—";
	return new Date(ts * 1000).toISOString().slice(0, 19).replace("T", " ");
}

function statusBadge(status: ReferralStatus): string {
	if (status === "active")
		return "inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700";
	if (status === "pending")
		return "inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700";
	return "inline-flex rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500";
}

function ProgramCard() {
	const programQ = useAffiliateReferralProgram();
	const upsertMut = useAffiliateReferralProgramUpsert();

	const [enabled, setEnabled] = useState(false);
	const [maxDepth, setMaxDepth] = useState(1);
	const [attrWin, setAttrWin] = useState(30);
	const [termsUrl, setTermsUrl] = useState("");
	const [tiersText, setTiersText] = useState("[]");
	const [dirty, setDirty] = useState(false);

	// Initial hydrate once the program arrives.
	useMemo(() => {
		if (!dirty && programQ.data) {
			setEnabled(programQ.data.enabled);
			setMaxDepth(programQ.data.max_depth);
			setAttrWin(programQ.data.attribution_window);
			setTermsUrl(programQ.data.terms_url);
			setTiersText(JSON.stringify(programQ.data.tiers, null, 2));
		}
	}, [programQ.data, dirty]);

	const handleSave = (e: FormEvent) => {
		e.preventDefault();
		let tiers: Tier[] = [];
		try {
			const parsed = JSON.parse(tiersText);
			if (Array.isArray(parsed)) tiers = parsed as Tier[];
		} catch {
			/* let BE reject */
		}
		upsertMut.mutate(
			{
				enabled,
				tiers,
				max_depth: maxDepth,
				attribution_window: attrWin,
				terms_url: termsUrl,
			},
			{
				onSuccess: () => setDirty(false),
			},
		);
	};

	return (
		<section className={CARD} aria-label="Program config">
			<h2 className="text-xl font-semibold text-zinc-900">Program config</h2>
			<p className="mt-1 text-xs text-zinc-500">
				Enable the program, set per-depth bonus tiers (bps), max recruit depth,
				and attribution window.
			</p>

			{programQ.isLoading ? (
				<p
					className="mt-4 text-sm text-zinc-500"
					data-testid="affiliate-referral-program-program-loading"
				>
					Loading.
				</p>
			) : programQ.error ? (
				<p className="mt-4 text-sm text-red-600" role="alert">
					{programQ.error.message}
				</p>
			) : (
				<form
					onSubmit={handleSave}
					className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
					data-testid="affiliate-referral-program-form"
				>
					<label className="flex items-center gap-2 text-sm text-zinc-900 sm:col-span-2">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-zinc-300 text-green-500 focus:ring-green-500"
							checked={enabled}
							onChange={(e) => {
								setEnabled(e.currentTarget.checked);
								setDirty(true);
							}}
							data-testid="affiliate-referral-program-enabled"
						/>
						Enable program
					</label>

					<div>
						<label className={LABEL}>Max depth</label>
						<input
							type="number"
							min={1}
							max={10}
							className={INPUT}
							value={maxDepth}
							onChange={(e) => {
								setMaxDepth(Number(e.currentTarget.value));
								setDirty(true);
							}}
							data-testid="affiliate-referral-program-max-depth"
						/>
					</div>

					<div>
						<label className={LABEL}>Attribution window (days)</label>
						<input
							type="number"
							min={1}
							max={365}
							className={INPUT}
							value={attrWin}
							onChange={(e) => {
								setAttrWin(Number(e.currentTarget.value));
								setDirty(true);
							}}
							data-testid="affiliate-referral-program-attr-window"
						/>
					</div>

					<div className="sm:col-span-2">
						<label className={LABEL}>Terms URL</label>
						<input
							type="url"
							placeholder="https://example.com/terms"
							className={INPUT}
							value={termsUrl}
							onChange={(e) => {
								setTermsUrl(e.currentTarget.value);
								setDirty(true);
							}}
							data-testid="affiliate-referral-program-terms-url"
						/>
					</div>

					<div className="sm:col-span-2">
						<label className={LABEL}>
							Tiers JSON — [{"{level,bonus_bps}"}, .]
						</label>
						<textarea
							rows={4}
							className={`${INPUT} font-mono`}
							value={tiersText}
							onChange={(e) => {
								setTiersText(e.currentTarget.value);
								setDirty(true);
							}}
							data-testid="affiliate-referral-program-tiers"
						/>
					</div>

					<div className="sm:col-span-2 flex items-center justify-end gap-3">
						{upsertMut.error ? (
							<p className="text-xs text-red-600" role="alert">
								{upsertMut.error.message}
							</p>
						) : null}
						<button
							type="submit"
							className={BTN}
							disabled={upsertMut.isPending}
							data-testid="affiliate-referral-program-save"
						>
							{upsertMut.isPending ? "Saving." : "Save"}
						</button>
					</div>
				</form>
			)}
		</section>
	);
}

function ReferralsCard() {
	const [statusFilter, setStatusFilter] = useState<ReferralStatus | "">("");
	const referralsQ = useAffiliateReferralsList(
		statusFilter ? { status: statusFilter } : undefined,
	);
	const createMut = useAffiliateReferralCreate();
	const updateMut = useAffiliateReferralUpdate();

	const [referrerId, setReferrerId] = useState("");
	const [referredId, setReferredId] = useState("");
	const [code, setCode] = useState("");

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!referrerId.trim() || !referredId.trim()) return;
		createMut.mutate(
			{
				referrer_id: referrerId.trim(),
				referred_id: referredId.trim(),
				code: code.trim() || undefined,
				status: "pending",
			},
			{
				onSuccess: () => {
					setReferrerId("");
					setReferredId("");
					setCode("");
				},
			},
		);
	};

	const rows = referralsQ.data?.referrals ?? [];

	return (
		<section className={CARD} aria-label="Referrals">
			<div className="flex items-center justify-between gap-4">
				<div>
					<h2 className="text-xl font-semibold text-zinc-900">Referrals</h2>
					<p className="mt-1 text-xs text-zinc-500">
						Recruit edges. Activate or revoke to change accrual eligibility.
					</p>
				</div>
				<select
					className={`${SELECT} w-auto`}
					value={statusFilter}
					onChange={(e) =>
						setStatusFilter(e.currentTarget.value as ReferralStatus | "")
					}
					data-testid="affiliate-referral-program-status-filter"
				>
					<option value="">All statuses</option>
					<option value="pending">Pending</option>
					<option value="active">Active</option>
					<option value="revoked">Revoked</option>
				</select>
			</div>

			<form
				onSubmit={handleCreate}
				className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4"
				data-testid="affiliate-referral-program-create-form"
			>
				<input
					type="text"
					placeholder="Referrer ID"
					className={INPUT}
					value={referrerId}
					onChange={(e) => setReferrerId(e.currentTarget.value)}
					data-testid="affiliate-referral-program-referrer-input"
				/>
				<input
					type="text"
					placeholder="Referred ID"
					className={INPUT}
					value={referredId}
					onChange={(e) => setReferredId(e.currentTarget.value)}
					data-testid="affiliate-referral-program-referred-input"
				/>
				<input
					type="text"
					placeholder="Code (optional)"
					className={INPUT}
					value={code}
					onChange={(e) => setCode(e.currentTarget.value)}
					data-testid="affiliate-referral-program-code-input"
				/>
				<button
					type="submit"
					className={BTN}
					disabled={
						createMut.isPending || !referrerId.trim() || !referredId.trim()
					}
					data-testid="affiliate-referral-program-create-submit"
				>
					{createMut.isPending ? "Adding." : "Add edge"}
				</button>
			</form>
			{createMut.error ? (
				<p className="mt-2 text-xs text-red-600" role="alert">
					{createMut.error.message}
				</p>
			) : null}

			<div className="mt-5">
				{referralsQ.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="affiliate-referral-program-loading"
					>
						Loading.
					</p>
				) : referralsQ.error ? (
					<p className="text-sm text-red-600" role="alert">
						{referralsQ.error.message}
					</p>
				) : rows.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="affiliate-referral-program-empty"
					>
						No referrals yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="min-w-full divide-y divide-zinc-200"
							data-testid="affiliate-referral-program-referrals-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Referrer</th>
									<th className={TH}>Referred</th>
									<th className={TH}>Status</th>
									<th className={TH}>Code</th>
									<th className={TH}>Created</th>
									<th className={TH}></th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{rows.map((r) => (
									<tr
										key={r.id}
										data-testid={`affiliate-referral-program-row-${r.id}`}
									>
										<td className={`${TD} font-mono text-xs`}>
											{r.referrer_id}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{r.referred_id}
										</td>
										<td className={TD}>
											<span className={statusBadge(r.status)}>{r.status}</span>
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{r.code || "—"}
										</td>
										<td className={`${TD} text-xs text-zinc-600`}>
											{formatUnix(r.created_at)}
										</td>
										<td className={`${TD} text-right`}>
											{r.status !== "active" ? (
												<button
													type="button"
													className={BTN_GHOST}
													onClick={() =>
														updateMut.mutate({
															id: r.id,
															body: { status: "active" },
														})
													}
													disabled={updateMut.isPending}
													data-testid={`affiliate-referral-program-activate-${r.id}`}
												>
													Activate
												</button>
											) : null}
											{r.status !== "revoked" ? (
												<button
													type="button"
													className={`${BTN_GHOST} ml-2`}
													onClick={() =>
														updateMut.mutate({
															id: r.id,
															body: { status: "revoked" },
														})
													}
													disabled={updateMut.isPending}
													data-testid={`affiliate-referral-program-revoke-${r.id}`}
												>
													Revoke
												</button>
											) : null}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</section>
	);
}

function LedgerCard() {
	const [ancestorFilter, setAncestorFilter] = useState("");
	const ledgerQ = useAffiliateReferralLedger(
		ancestorFilter.trim()
			? { ancestor_id: ancestorFilter.trim(), limit: 100 }
			: { limit: 100 },
	);
	const rows = ledgerQ.data?.ledger ?? [];
	const totalBonus = useMemo(
		() => rows.reduce((s, r) => s + r.bonus_cents, 0),
		[rows],
	);

	return (
		<section className={CARD} aria-label="Ledger">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h2 className="text-xl font-semibold text-zinc-900">Bonus ledger</h2>
					<p className="mt-1 text-xs text-zinc-500">
						Every accrual row. Filter by ancestor to inspect a single
						affiliate's earnings.
					</p>
				</div>
				<div className="flex flex-col gap-1">
					<label className={LABEL}>Filter by ancestor</label>
					<input
						type="text"
						placeholder="affiliate_id"
						className={INPUT}
						value={ancestorFilter}
						onChange={(e) => setAncestorFilter(e.currentTarget.value)}
						data-testid="affiliate-referral-program-ancestor-filter"
					/>
				</div>
			</div>

			<div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
				<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
					<p className="text-xs text-zinc-500">Rows</p>
					<p
						className="text-lg font-semibold text-zinc-900"
						data-testid="affiliate-referral-program-ledger-count"
					>
						{ledgerQ.data?.count ?? 0}
					</p>
				</div>
				<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
					<p className="text-xs text-zinc-500">Total bonus</p>
					<p
						className="text-lg font-semibold text-green-700"
						data-testid="affiliate-referral-program-ledger-total"
					>
						{formatCents(totalBonus)}
					</p>
				</div>
				<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
					<p className="text-xs text-zinc-500">Limit</p>
					<p className="text-lg font-semibold text-zinc-900">
						{ledgerQ.data?.limit ?? 100}
					</p>
				</div>
			</div>

			<div className="mt-5">
				{ledgerQ.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="affiliate-referral-program-ledger-loading"
					>
						Loading.
					</p>
				) : ledgerQ.error ? (
					<p className="text-sm text-red-600" role="alert">
						{ledgerQ.error.message}
					</p>
				) : rows.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="affiliate-referral-program-ledger-empty"
					>
						No ledger rows yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="min-w-full divide-y divide-zinc-200"
							data-testid="affiliate-referral-program-ledger-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Ancestor</th>
									<th className={TH}>Descendant</th>
									<th className={TH}>Depth</th>
									<th className={TH}>Bps</th>
									<th className={TH}>Base</th>
									<th className={TH}>Bonus</th>
									<th className={TH}>Source</th>
									<th className={TH}>When</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{rows.map((r) => (
									<tr
										key={r.id}
										data-testid={`affiliate-referral-program-ledger-row-${r.id}`}
									>
										<td className={`${TD} font-mono text-xs`}>
											{r.ancestor_id}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{r.descendant_id}
										</td>
										<td className={TD}>{r.depth}</td>
										<td className={TD}>{r.bonus_bps}</td>
										<td className={TD}>{formatCents(r.base_payout_cents)}</td>
										<td className={`${TD} font-medium text-green-700`}>
											{formatCents(r.bonus_cents)}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{r.source_ref || "—"}
										</td>
										<td className={`${TD} text-xs text-zinc-600`}>
											{formatUnix(r.created_at)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</section>
	);
}

function SummaryCard() {
	const [affiliateId, setAffiliateId] = useState("");
	const [submittedId, setSubmittedId] = useState("");
	const summaryQ = useAffiliateReferralSummary(
		submittedId,
		submittedId.length > 0,
	);

	const handleLookup = (e: FormEvent) => {
		e.preventDefault();
		setSubmittedId(affiliateId.trim());
	};

	return (
		<section className={CARD} aria-label="Per-affiliate summary">
			<h2 className="text-xl font-semibold text-zinc-900">Affiliate summary</h2>
			<p className="mt-1 text-xs text-zinc-500">
				Look up direct-referral counts + bonus totals for a single affiliate.
			</p>

			<form
				onSubmit={handleLookup}
				className="mt-4 flex flex-col gap-3 sm:flex-row"
				data-testid="affiliate-referral-program-summary-form"
			>
				<input
					type="text"
					placeholder="affiliate_id"
					className={INPUT}
					value={affiliateId}
					onChange={(e) => setAffiliateId(e.currentTarget.value)}
					data-testid="affiliate-referral-program-summary-input"
				/>
				<button
					type="submit"
					className={BTN_OUTLINE}
					disabled={!affiliateId.trim()}
					data-testid="affiliate-referral-program-summary-submit"
				>
					Look up
				</button>
			</form>

			{submittedId ? (
				summaryQ.isLoading ? (
					<p className="mt-4 text-sm text-zinc-500">Loading.</p>
				) : summaryQ.error ? (
					<p className="mt-4 text-sm text-red-600" role="alert">
						{summaryQ.error.message}
					</p>
				) : summaryQ.data ? (
					<div
						className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4"
						data-testid="affiliate-referral-program-summary-result"
					>
						<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
							<p className="text-xs text-zinc-500">Direct referrals</p>
							<p className="text-lg font-semibold text-zinc-900">
								{summaryQ.data.direct_referrals}
							</p>
						</div>
						<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
							<p className="text-xs text-zinc-500">Active</p>
							<p className="text-lg font-semibold text-green-700">
								{summaryQ.data.by_status.active}
							</p>
						</div>
						<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
							<p className="text-xs text-zinc-500">Bonus rows</p>
							<p className="text-lg font-semibold text-zinc-900">
								{summaryQ.data.bonus_rows}
							</p>
						</div>
						<div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
							<p className="text-xs text-zinc-500">Total bonus</p>
							<p className="text-lg font-semibold text-green-700">
								{formatCents(summaryQ.data.total_bonus_cents)}
							</p>
						</div>
					</div>
				) : null
			) : null}
		</section>
	);
}

export function AffiliateReferralProgramPage() {
	return (
		<main
			data-testid="affiliate-referral-program-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Affiliate referral program
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-2xl font-semibold text-transparent">
					Multi-level referral bonuses
				</h1>
				<p className="text-sm text-zinc-500">
					Configure recruit bonuses, record referral edges, and inspect the
					per-affiliate bonus ledger.
				</p>
			</header>

			<ProgramCard />
			<ReferralsCard />
			<LedgerCard />
			<SummaryCard />
		</main>
	);
}

export default AffiliateReferralProgramPage;
